import { DESCRIPTION_COPY_LIMITS, TITLE_COPY_LIMITS } from "../copy-policy";
/**
 * Carousel — the crossfading band of scenes. Data contract + slot schema.
 *
 * ONE block-level field, the carousel's name, and everything else belongs to a
 * scene. The name is never drawn: it names the region for a visitor listing the
 * page's landmarks, which is why it is required and why deriving it from the
 * first scene was rejected — reordering the scenes would then rename the
 * landmark.
 *
 * THE SCENES ARE A TYPED REPEAT GROUP, the first in the product. Each scene has
 * a KIND, chosen when it is added and fixed thereafter, and the kind decides how
 * many subjects the scene asks for and where each one sits. See
 * {@link RepeatVariants}: the group declares the UNION of every kind's picture
 * slots, because that union is what the API's declaration guard reads, and the
 * kind narrows it in the editor.
 *
 * The kind is NOT a scalar slot. That absence is deliberate and load-bearing —
 * a declared scalar renders a free-text input, which is precisely the
 * "type the category by hand" failure this design removes.
 *
 * The rules live in `../carousel`.
 */
import { CAROUSEL_MINIMUM_SCENES, CAROUSEL_SCENES_KEY, CAROUSEL_SCENE_KINDS, CAROUSEL_SCENE_KIND_KEY, CAROUSEL_SCREENS, CAROUSEL_SUBJECT_SLOTS, carouselSlotKey, } from "../carousel";
export const carouselSchema = {
    componentId: "Carousel",
    scalarSlots: [{ key: "label", label: "Carousel name", type: "text" }],
    mediaSlots: [],
    repeatSlots: [
        {
            key: CAROUSEL_SCENES_KEY,
            label: "Scenes",
            itemLabel: "Scene",
            // A UI hint only, and the same number the validator enforces for real.
            minItems: CAROUSEL_MINIMUM_SCENES,
            scalarSlots: [
                {
                    key: "title",
                    ...TITLE_COPY_LIMITS,
                    label: "Title",
                    type: "text",
                },
                {
                    key: "description",
                    ...DESCRIPTION_COPY_LIMITS,
                    label: "Description",
                    type: "textarea",
                },
            ],
            // The UNION. Which of these a scene offers is its kind's business.
            mediaSlots: CAROUSEL_SUBJECT_SLOTS,
            variants: {
                key: CAROUSEL_SCENE_KIND_KEY,
                // Built from the one declaration in `../carousel`, through the same
                // `carouselSlotKey` the union is built with — so a kind cannot come to
                // name a slot the group does not declare.
                options: CAROUSEL_SCENE_KINDS.map((kind) => ({
                    id: kind.id,
                    legacy: kind.legacy,
                    label: kind.label,
                    description: kind.description,
                    mediaSlotKeys: kind.layers.flatMap((layerId) => CAROUSEL_SCREENS.map((screen) => carouselSlotKey(layerId, screen))),
                })),
            },
        },
    ],
};
//# sourceMappingURL=carousel.js.map